import type { TFunction } from 'i18next';
import type { FetchBaseQueryError } from '@reduxjs/toolkit/query/react';

const INVALID_CREDENTIAL = 401;
const PASSWORD_ALREADY_USED = 422;

const isFetchBaseQueryError = (err: unknown): err is FetchBaseQueryError =>
  typeof err === 'object' && err !== null && 'status' in err;

/**
 * Traduce el rechazo de `POST /auth/recoverPassword` a algo que el vendedor
 * pueda leer.
 *
 * - `401`: el permiso venció o no es válido. No es un error de credenciales, y
 *   el servicio lo decide antes de mirar la contraseña.
 * - `422`: la contraseña está en el historial. Es la excepción documentada a
 *   «no mostrar el error del backend»: el mensaje llega en español, apto para
 *   pantalla, y sólo el servicio conoce el historial — HU-CUDL-089 / CA-09.
 * - Cualquier otro caso, incluida la falla de transporte, es un error genérico.
 */
export const resolveResetError = (err: unknown, t: TFunction): string => {
  if (!isFetchBaseQueryError(err)) return t('errors.apiError');

  if (err.status === INVALID_CREDENTIAL) return t('errors.sessionExpired');

  if (err.status === PASSWORD_ALREADY_USED) {
    const body = err.data as { message?: unknown } | undefined;
    const message =
      typeof body?.message === 'string' ? body.message.trim() : '';
    return message || t('errors.apiError');
  }

  return t('errors.apiError');
};
